// La página (y la ventana) de notificaciones.
//
// Observación 41 (31 oct 2026): antes se suscribía a TODAS las notificaciones del alumno más las de
// «todos», sin límite y sin cerrar la escucha, y esa ventana se montaba en cada página del sitio.
// Ahora: la primera página (20) llega en vivo y se cierra al desmontar; «Ver anteriores» trae de
// 20 en 20 con un cursor; «marcar todas como leídas» actualiza en lotes las no leídas que haya en
// la base, no solo las cargadas.
import NotificationCard from "@components/NavbarCustom/components/Notification/NotificationCard";
import { QueryConstraint, QueryDocumentSnapshot, collection, doc, getDocs, limit, onSnapshot, orderBy, query, startAfter, where, writeBatch } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { db } from "../../initFirebase";
import "./NotificationsStyles.scss";

export const NOTIFICACIONES_POR_PAGINA = 20;

/** El filtro de la consulta según quién mira (el mismo criterio de siempre). */
function filtroDe(user: any, userMode: boolean): QueryConstraint[] {
	if (user.userType !== 4) {
		return [where("userId", "in", [user.id, user.userType === 2 && !userMode ? "admin" : "all"])];
	}
	return !userMode ? [where("sectionId", "in", user.permissions || [])] : [where("userId", "==", user.id)];
}

function NotificationContainer({ user, notificationContainerRef }) {
	const [notifications, setNotifications] = useState<Array<any>>([]); // la primera página, en vivo
	const [anteriores, setAnteriores] = useState<Array<any>>([]); // las páginas que se pidieron con «Ver anteriores»
	const [userMode, setUserMode] = useState<boolean>(false);
	const [cargandoMas, setCargandoMas] = useState(false);
	const [hayMas, setHayMas] = useState(true);
	const cursor = useRef<QueryDocumentSnapshot | null>(null); // el último doc cargado, para la siguiente página
	const hayAnteriores = useRef(false); // mientras no se pidan páginas viejas, el cursor sigue a la primera página en vivo

	useEffect(() => {
		if (!user) return;
		setAnteriores([]);
		cursor.current = null;
		hayAnteriores.current = false;
		setHayMas(true);
		const q = query(collection(db, "notifications"), ...filtroDe(user, userMode), orderBy("date", "desc"), limit(NOTIFICACIONES_POR_PAGINA));
		const unsubscribe = onSnapshot(
			q,
			(snap) => {
				const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
				setNotifications(lista);
				if (!hayAnteriores.current) cursor.current = snap.docs[snap.docs.length - 1] || null;
				if (snap.docs.length < NOTIFICACIONES_POR_PAGINA) setHayMas(false);
			},
			(e) => console.error("notificaciones", e)
		);
		return () => unsubscribe();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id, userMode]);

	const verAnteriores = async () => {
		if (!cursor.current || cargandoMas) return;
		setCargandoMas(true);
		try {
			const q = query(collection(db, "notifications"), ...filtroDe(user, userMode), orderBy("date", "desc"), startAfter(cursor.current), limit(NOTIFICACIONES_POR_PAGINA));
			const snap = await getDocs(q);
			cursor.current = snap.docs[snap.docs.length - 1] || cursor.current;
			hayAnteriores.current = true;
			setAnteriores((prev) => prev.concat(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
			if (snap.docs.length < NOTIFICACIONES_POR_PAGINA) setHayMas(false);
		} catch (e) {
			console.error("notificaciones anteriores", e);
		} finally {
			setCargandoMas(false);
		}
	};

	const seenAll = async () => {
		if (!window.confirm("¿Estás seguro de marcar todas como leídas?")) return;
		try {
			// todas las no leídas que haya en la base (no solo las de la pantalla), en lotes de 400
			const snap = await getDocs(query(collection(db, "notifications"), ...filtroDe(user, userMode), where("seen", "==", false), limit(2000)));
			for (let i = 0; i < snap.docs.length; i += 400) {
				const lote = writeBatch(db);
				snap.docs.slice(i, i + 400).forEach((d) => lote.update(doc(db, "notifications", d.id), { seen: true }));
				await lote.commit();
			}
			setAnteriores((prev) => prev.map((n) => ({ ...n, seen: true })));
		} catch (e) {
			console.error("marcar todas como leídas", e);
		}
	};

	const vistas = notifications.concat(anteriores.filter((a) => !notifications.some((n) => n.id === a.id)));

	return (
		<div className="notifications-container-main-component" ref={notificationContainerRef}>
			<div>
				<h1>Notificaciones</h1>
			</div>
			{user !== null && user !== undefined && (user.userType === 2 || user.userType === 4) && (
				<Form className="translate-input-container mt-2">
					<Form.Check
						id="custom-switch-2"
						checked={userMode}
						onChange={(e) => setUserMode(e.target.checked)}
						label={user.userType === 2 ? "Admin" : "Colaborador"}
					/>
					<Form.Check
						type="switch"
						id="custom-switch-2"
						checked={userMode}
						onChange={(e) => setUserMode(e.target.checked)}
						label={"Usuario"}
					/>
				</Form>
			)}
			<div style={{ padding: "5px 25px", display: "flex", justifyContent: "flex-end" }}>
				<span
					onClick={seenAll}
					style={{
						color: "#ca5050",
						cursor: "pointer",
						textAlign: "end",
						border: "1px solid #ca5050",
						borderRadius: 10,
						padding: "2px 5px",
					}}
				>
					Marcar todas como leídas
				</span>
			</div>
			<div>
				<div className="notification-main-container">
					{vistas.map((notification) => (
						<NotificationCard user={user} notification={notification} key={notification.id} />
					))}
					{vistas.length === 0 && <p className="notificaciones-vacio">No tienes notificaciones.</p>}
				</div>
				{hayMas && vistas.length > 0 && (
					<div className="notificaciones-pie">
						<button type="button" className="notificaciones-mas" onClick={verAnteriores} disabled={cargandoMas}>
							{cargandoMas ? "Cargando…" : "Ver anteriores"}
						</button>
					</div>
				)}
			</div>
		</div>
	);
}

export default NotificationContainer;
